import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Accordion } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutDashboard, ClipboardCheck, Users, FolderOpen, ListTodo, Save } from "lucide-react";
import { AuditSummary, openCount } from "@/components/AuditSummary";
import { AssuranceHeader } from "@/components/evidence/AssuranceHeader";
import { AssuranceTrend } from "@/components/evidence/AssuranceTrend";
import { KeyDocuments } from "@/components/evidence/KeyDocuments";
import { Overview } from "@/components/evidence/Overview";
import { OverviewDashboard } from "@/components/evidence/OverviewDashboard";
import { buildPriorities } from "@/components/evidence/NextPriorities";
import { PracticeAreas } from "@/components/evidence/PracticeAreas";
import { ReviewStepper, StepNav } from "@/components/evidence/ReviewStepper";
import { SignOffSummary, readiness } from "@/components/evidence/SignOffSummary";
import { RoundHeader, PreviousRounds } from "@/components/evidence/AuditRounds";
import { CarePlanReviews, DentalReferrals } from "@/components/evidence/RecordsLog";
import { ResidentTimeline } from "@/components/evidence/ResidentTimeline";
import { ResidentAuditItem } from "@/components/evidence/ResidentAuditItem";
import { MonthlyAuditBanner } from "@/components/evidence/MonthlyAuditBanner";
import { ChampionCertificate } from "@/components/ChampionCertificate";
import MonthlyCheck from "@/pages/dashboard/MonthlyCheck";
import { useEvidenceChecklist } from "@/hooks/useEvidenceChecklist";
import { residentStats, residentImprovements, liveScore, allFindings, practiceLive, blendScore } from "@/lib/auditScoring";
import { ragOf } from "@/lib/rag";

const EMPTY_RESIDENT = { label: "", answers: {} };
const TABS = ["overview", "review", "residents", "evidence", "actions"];
// Legacy deep links (reminder emails, banners) → tab + anchor
const LEGACY = { "audit=residents": ["residents", "resident-audit"], "review=overview": ["review", "review-overview"], "records=referrals": ["residents", "dental-referrals"] };

const SectionTitle = ({ step, title, sub }) => (
  <div className="mb-3">
    <h4 className="font-heading font-bold text-brand text-sm">{step ? `Step ${step} — ` : ""}{title}</h4>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export default function EvidenceChecklist({ training, trainingPanel }) {
  const c = useEvidenceChecklist();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { tpl, areas, practice, residents, resDomains, sampleSize, sampleInfo } = c;
  const [tab, setTab] = useState(() => (TABS.includes(params.get("tab")) ? params.get("tab") : "overview"));
  const [pendingAnchor, setPendingAnchor] = useState(null);
  const [step, setStepState] = useState(() => { const n = Number(params.get("step")); return n >= 1 && n <= 3 ? n : 1; });

  // Anchors inside the Assurance Review belong to a specific step
  const stepForAnchor = (anchor) => (!anchor ? null : anchor === "review-overview" ? 1 : anchor.startsWith("practice-") ? 2 : anchor === "signoff-summary" ? 3 : null);

  const go = useCallback((target, anchor) => {
    if (typeof target === "string" && target.startsWith("/")) { nav(target); return; }
    setTab(target);
    const next = new URLSearchParams(params); next.set("tab", target);
    const st = target === "review" ? stepForAnchor(anchor) : null;
    if (st) { setStepState(st); next.set("step", String(st)); }
    setParams(next, { replace: true });
    setPendingAnchor(anchor || null);
  }, [nav, params, setParams]);

  const setStep = useCallback((n, anchor) => {
    setStepState(n);
    const next = new URLSearchParams(params); next.set("tab", "review"); next.set("step", String(n)); setParams(next, { replace: true });
    setPendingAnchor(anchor || "review-top");
  }, [params, setParams]);

  // Resolve legacy ?audit= / ?review= / ?records= links once data is loaded
  useEffect(() => {
    if (!tpl.length) return;
    for (const [q, [t, a]] of Object.entries(LEGACY)) {
      const [k, v] = q.split("=");
      if (params.get(k) === v) {
        const next = new URLSearchParams(params); next.delete(k); next.set("tab", t);
        const st = t === "review" ? stepForAnchor(a) : null;
        if (st) { setStepState(st); next.set("step", String(st)); }
        setParams(next, { replace: true }); setTab(t); setPendingAnchor(a); return;
      }
    }
  }, [tpl.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingAnchor) return undefined;
    const scroll = () => { const el = document.getElementById(pendingAnchor); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
    const t1 = setTimeout(scroll, 50);
    const t2 = setTimeout(() => { scroll(); setPendingAnchor(null); }, 350);  // re-run once layout has settled
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [pendingAnchor, tab]);

  // Warn before leaving the page with unsaved changes (tab switches keep state in this component)
  useEffect(() => {
    if (!c.dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [c.dirty]);

  const isRes = (key) => resDomains.includes(key);
  const resTpl = tpl.filter((d) => isRes(d.key));
  const resItemsTotal = resTpl.reduce((n, d) => n + d.items.length, 0);
  const resident = (k) => residents[k] || EMPTY_RESIDENT;
  const residentKeys = Array.from({ length: sampleSize }, (_, i) => String(i + 1));
  const stats = Object.fromEntries(residentKeys.map((k) => [k, residentStats(resident(k), resTpl, resItemsTotal)]));
  const audited = residentKeys.filter((k) => stats[k].status !== "not_started");

  const res = liveScore({ tpl: resTpl, isRes, residents, audited });
  const pr = practiceLive(areas, practice);
  const live = { ...res, overall: blendScore(pr.pct, res.residentPct), practicePct: pr.pct, practiceAnswered: pr.answered, practiceTotal: pr.total,
    domains: [...pr.domains, ...res.domains], actions: res.actions + pr.actions };
  const findings = [...pr.findings, ...allFindings({ tpl: resTpl, isRes, answers: {}, residents, residentKeys }), ...(c.carriedFindings || [])];
  const openRows = residentKeys.map((k) => ({ k, label: resident(k).label || `Resident ${k}`, date_audited: resident(k).date_audited || "", status: stats[k].status }));
  const assessed = pr.answered > 0 || audited.length > 0;
  // The working assessment is only "complete" once every practice area is confirmed and the full resident sample is audited
  const complete = pr.answered === pr.total && pr.total > 0 && audited.length >= sampleSize;
  const liveRag = assessed ? ragOf(live.overall) : null;

  const priorities = useMemo(() => buildPriorities({ overview: c.overview, assurance: c.assurance, live, audited: audited.length, sampleSize, monthlyDone: c.monthlyDone, monthlyCount: c.monthlyCount,
    practice, areas, findings, plan: c.plan, keyDocsTpl: c.keyDocsTpl, keyDocs: c.keyDocs }), [c.overview, c.assurance, audited.length, sampleSize, c.monthlyDone, c.monthlyCount, practice, areas, findings.length, c.plan, c.keyDocsTpl, c.keyDocs]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmedAreas = areas.filter((a) => practice[a.key]?.status).length;
  const gaps = c.overview ? [c.overview.training?.outstanding?.length, c.overview.training?.overdue_assignments, !c.overview.champion?.appointed, !c.overview.audits?.monthly_done, c.overview.documents?.overdue, (c.keyDocsTpl || []).some((k) => !c.keyDocs?.[k.key]), c.overview.care_plans?.overdue, c.overview.referrals?.overdue].filter(Boolean).length : 0;
  const ready = readiness({ areas, practice, openRows, residents, resTpl, sampleSize, currentRound: c.currentRound, closedRounds: c.closedRounds, keyDocsTpl: c.keyDocsTpl, keyDocs: c.keyDocs, findings, plan: c.plan });
  const outstanding = ready.blocking;
  const stepStatus = {
    evidence: { done: step > 1 || confirmedAreas > 0, label: gaps ? `${gaps} item${gaps === 1 ? "" : "s"} to check` : "All evidence in place" },
    practice: { done: areas.length > 0 && confirmedAreas === areas.length, label: `${confirmedAreas} of ${areas.length} areas confirmed` },
    signoff: { done: c.assurance?.last_score != null && outstanding === 0, label: `${openCount(findings, c.plan)} open action${openCount(findings, c.plan) === 1 ? "" : "s"}${outstanding ? ` · ${outstanding} blocking` : ""}` },
  };

  const headerProps = { live, rag: liveRag, assessed, complete, assurance: c.assurance, audited: audited.length, sampleSize, saving: c.saving, dirty: c.dirty, completing: c.completing, onSave: c.save, onComplete: c.completeAssessment, canSignOff: ready.canSignOff };

  if (!tpl.length && c.loadError) return (
    <div data-testid="compliance-load-error" className="rounded-2xl border border-rag-red/30 bg-rag-red-bg p-6 text-center">
      <div className="font-heading font-bold text-brand">We couldn't load your Compliance dashboard</div>
      <p className="text-sm text-muted-foreground mt-1">{c.loadError}</p>
      <Button data-testid="compliance-retry-btn" onClick={() => c.reload()} className="mt-4 rounded-full bg-brand hover:bg-brand-hover text-white">Try again</Button>
    </div>
  );
  if (!tpl.length) return <div data-testid="compliance-loading" className="grid place-items-center py-24 text-muted-foreground">Loading your assurance data…</div>;

  return (
    <div>
      <Tabs value={tab} onValueChange={(v) => go(v)}>
        <TabsList data-testid="compliance-tabs" className="mb-6 flex w-full overflow-x-auto justify-start h-auto p-1 gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview" className="shrink-0"><LayoutDashboard className="h-4 w-4 mr-2" />Overview</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review" className="shrink-0"><ClipboardCheck className="h-4 w-4 mr-2" />Assurance Review</TabsTrigger>
          <TabsTrigger value="residents" data-testid="tab-residents" className="shrink-0"><Users className="h-4 w-4 mr-2" />Resident Audits</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tab-evidence" className="shrink-0"><FolderOpen className="h-4 w-4 mr-2" />Evidence & Training</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions" className="shrink-0"><ListTodo className="h-4 w-4 mr-2" />Action Plan{findings.length ? <span className="ml-2 text-[11px] font-bold bg-coral text-white rounded-full px-1.5 py-0.5 leading-none" data-testid="tab-actions-count">{findings.length}</span> : null}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewDashboard live={live} liveRag={liveRag} assessed={assessed} complete={complete} assurance={c.assurance} audited={audited.length} sampleSize={sampleSize} monthlyDone={c.monthlyDone} monthlyCount={c.monthlyCount} currentRound={c.currentRound}
            overview={c.overview} keyDocsTpl={c.keyDocsTpl} keyDocs={c.keyDocs} training={training} priorities={priorities} onGo={go} />
        </TabsContent>

        <TabsContent value="review">
          <div id="review-top" className="scroll-mt-24" />
          <AssuranceHeader {...headerProps} />
          <ReviewStepper step={step} onStep={(n) => setStep(n)} status={stepStatus} />
          {step === 1 && (
            <div data-testid="review-step-1-content">
              <div className="rounded-xl bg-brand-light/60 border border-brand/10 px-4 py-3 mb-4 text-sm text-brand" data-testid="review-step-1-intro">
                <b>Nothing to fill in here.</b> This evidence is pulled automatically from your training, audit, document and resident records. Check anything flagged <span className="inline-block text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rag-amber-bg text-rag-amber align-middle">Needs checking</span>, then continue.
              </div>
              <Overview overview={c.overview} flagGaps onGo={go} />
              <AssuranceTrend history={c.assurance.history} />
              <StepNav step={1} onStep={setStep} nextLabel="Continue to confirm practice" />
            </div>
          )}
          {step === 2 && areas.length > 0 && (
            <div data-testid="review-step-2-content">
              <PracticeAreas areas={areas} practice={practice} onChange={c.setPracticeField} guided openKey={areas.some((a) => `practice-${a.key}` === pendingAnchor) ? pendingAnchor.replace("practice-", "") : null} />
              <StepNav step={2} onStep={setStep} nextLabel="Continue to actions & sign-off" />
            </div>
          )}
          {step === 3 && (
            <div data-testid="review-step-3-content">
              <SignOffSummary live={live} liveRag={liveRag} assessed={assessed} complete={complete} assurance={c.assurance} areas={areas} practice={practice}
                openRows={openRows} residents={residents} resTpl={resTpl} sampleSize={sampleSize} currentRound={c.currentRound} closedRounds={c.closedRounds} keyDocsTpl={c.keyDocsTpl} keyDocs={c.keyDocs} findings={findings} plan={c.plan} onPlan={c.setPlanField}
                onComplete={c.completeAssessment} completing={c.completing} onGo={(n, anchor) => setStep(n, anchor)} onGoResidents={() => go("residents", "resident-audit")} />
              <StepNav step={3} onStep={setStep} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="residents">
          <MonthlyAuditBanner done={c.monthlyDone} monthlyCount={c.monthlyCount} audited={audited.length} sampleSize={sampleSize} />
          <RoundHeader currentRound={c.currentRound} audited={audited.length} sampleSize={sampleSize} dirty={c.dirty} onStart={c.startRound} starting={c.startingRound} />
          <div id="resident-audit" className="flex flex-wrap items-center justify-between gap-3 mb-3 scroll-mt-24">
            <div>
              <h4 className="font-heading font-bold text-brand text-sm">Monthly resident audit</h4>
              <p className="text-xs text-muted-foreground" data-testid="sample-explainer">
                16 questions × {sampleSize} residents{sampleInfo?.beds ? ` — about 15% of your ${sampleInfo.beds} beds` : " (add your bed count in Profile to size the sample)"}. Rotate residents each month and always include someone new, with dentures, resisting care or at end of life. Use a room number or initials — not full names.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" data-testid="sample-progress">{audited.length} / {sampleSize} audited</Badge>
              {sampleSize < (sampleInfo?.max || 15) && (
                <button type="button" data-testid="add-resident-btn" onClick={c.addResident} className="text-xs font-semibold text-coral hover:underline whitespace-nowrap">+ Audit another resident</button>
              )}
            </div>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="resident-accordion">
            {residentKeys.map((k) => (
              <ResidentAuditItem key={k} k={k} r={resident(k)} st={stats[k]} resTpl={resTpl} resItemsTotal={resItemsTotal}
                improvements={residentImprovements(resident(k), resTpl)}
                onAnswer={c.setResItem} onEvidence={c.setResAction} onAction={c.setResAction} onField={c.setResField} />
            ))}
          </Accordion>
          <PreviousRounds rounds={c.closedRounds} resTpl={resTpl} />

          <div className="mt-10">
            <SectionTitle title="Resident records" sub="Record once — care plan reviews and dental referrals logged here update the Overview automatically." />
            <CarePlanReviews onChanged={c.refreshDerived} />
            <DentalReferrals onChanged={c.refreshDerived} />
            <ResidentTimeline residents={c.timeline} />
          </div>

          <div id="monthly-check" className="mt-10 scroll-mt-24">
            <SectionTitle title="Monthly oral care check" sub="Twelve quick deterioration-focused checks for the whole home." />
            <MonthlyCheck />
          </div>
        </TabsContent>

        <TabsContent value="evidence">
          <div id="key-documents" className="scroll-mt-24">
            <KeyDocuments template={c.keyDocsTpl} docs={c.keyDocs} busyKey={c.docBusy} onUpload={c.uploadKeyDoc} onRemove={c.removeKeyDoc} />
          </div>
          <div className="mt-8"><ChampionCertificate /></div>
          <div id="staff-training" className="mt-8 scroll-mt-24">
            <SectionTitle title="Staff training" sub="Completion, overdue training and certificates across your team." />
            {trainingPanel}
          </div>
        </TabsContent>

        <TabsContent value="actions">
          {tpl.length > 0 && <AuditSummary live={live} assessed={assessed} complete={complete} tpl={tpl} findings={findings} plan={c.plan} onPlan={c.setPlanField} history={c.assurance.history || []}
            onComplete={c.completeAssessment} completing={c.completing} />}
        </TabsContent>
      </Tabs>

      {c.dirty && (
        <div data-testid="unsaved-bar" className="fixed top-[72px] sm:top-auto sm:bottom-4 left-4 sm:left-1/2 sm:-translate-x-1/2 z-50 flex items-center gap-3 rounded-full bg-brand text-white shadow-lg pl-4 sm:pl-5 pr-2 py-2 text-sm">
          <span className="font-medium whitespace-nowrap">Unsaved changes</span>
          <Button size="sm" data-testid="unsaved-save-btn" onClick={c.save} disabled={c.saving} className="rounded-full bg-coral hover:bg-coral-hover text-white h-8"><Save className="h-3.5 w-3.5 mr-1.5" />{c.saving ? "Saving…" : "Save progress"}</Button>
        </div>
      )}
    </div>
  );
}
